import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { HTMLAsset, ImageAsset, useAsset } from '@techstyle/react-assets';
import { Countdown } from '@techstyle/react-components-legacy';
import { FormattedMessage } from '@techstyle/react-intl';
import { parseDate } from '@techstyle/redux-core';

import { desktop } from '../../styles';
import { getMemberJoinDate } from '../../utils/selectors';
import { useShouldShowNewPricingSkinnyBanner } from '../../utils/useShouldShowNewPricingSkinnyBanner';
import { Container as AssetCountdown } from '../AssetCountdown';
import { Component as CountdownTimerHeader } from '../CountdownTimerHeader';
import { useCountdownTimer } from '../CountdownTimerProvider/CountdownTimerProvider';
import { Component as ProductHeaderFancy } from '../ProductHeaderFancy';
import { Component as QuizResultsHeaderAsset } from '../QuizResultsHeaderAsset';

/* ======================================== STYLES ======================================== */

const SimpleBanner = styled(ImageAsset)`
  display: ${props => (props.showMobile ? `flex` : `none`)};
  min-height: 200px;
  align-items: center;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;

  color: ${props => props.theme.colors.default};
  font-weight: normal;
  letter-spacing: 0.2px;
  text-align: center;

  h1 {
    font-size: ${32 / 12}em;
  }

  ${desktop`
    display: flex;
    min-height: 360px;

    h1 {
      font-size:  ${64 / 14}em;
    }
  `};
`;

const SectionWrapper = styled.section`
  ${props =>
    !props.isCountdownActive &&
    `margin-top: ${props.theme.spacing.moderate}px;`};
`;

const StyledCountdownTimerHeader = styled(CountdownTimerHeader)`
  & {
    margin: 18px;
    border-radius: 37px;
    font-size: 18px;
    font-weight: normal;
    background: ${props => props.theme.colors.default};
    color: ${props => props.theme.colors.white};
  }

  ${CountdownTimerHeader.CountdownMessage} {
    font-size: 12px;
  }

  ${CountdownTimerHeader.StyledCountdown} {
    padding: 0 0 0 12px;
  }

  ${Countdown.CountdownNumber} {
    font-weight: bold;
  }

  ${desktop`
    max-width: 336px;
    margin: 18px auto;
    padding: 11px;
  `};
`;

const CountdownTimerWrapper = styled.div`
  font-size: 18px;
  font-weight: normal;
  text-align: center;
  color: ${props => props.theme.colors.default};
  margin: 18px 0;

  @media screen and (min-width: ${props => props.theme.breakpoints.large}px) {
    font-size: 21px;
    margin: 18px 0 22px;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.xlarge}px) {
    font-size: 24px;
    margin: 21px 0 ${props => props.theme.spacing.moderate}px;
  }
`;

const StyledAssetCountdown = styled(AssetCountdown)`
  display: inline-block;
  width: auto;
  margin-bottom: 0;
  padding: 0 0 0 4px;

  & ${AssetCountdown.StyledCountdown} {
    font-size: 6px;
    margin-bottom: 0;

    @media screen and (min-width: ${props => props.theme.breakpoints.large}px) {
      font-size: 7px;
    }

    @media screen and (min-width: ${props =>
        props.theme.breakpoints.xlarge}px) {
      font-size: 8px;
    }
  }

  & ${Countdown.CountdownNumber} {
    font-size: 18px;

    @media screen and (min-width: ${props => props.theme.breakpoints.large}px) {
      font-size: 21px;
    }

    @media screen and (min-width: ${props =>
        props.theme.breakpoints.xlarge}px) {
      font-size: 24px;
    }
  }

  & ${Countdown.CountdownLabel} {
    display: none;
  }
`;

// We use a separate CSS variable because this style is being shared
// by the SearchSpringBanner component in SearchLayout
const skinnyBannerStyles = css`
  ${props => props.theme.assets.htmlStyles};
  padding: ${props => props.theme.spacing.tiny}px
    ${props => props.theme.spacing.medium}px;

  background-color: ${props => (props.bgColor ? props.bgColor : 'transparent')};
  text-align: center;

  ${desktop`
    padding: ${props => props.theme.spacing.small}px
      ${props => props.theme.spacing.medium}px;
  `};
`;

const SkinnyBanner = styled.div`
  ${skinnyBannerStyles}
`;

ProductHeader.propTypes = {
  className: PropTypes.string,
  hidePromo: PropTypes.bool,
};
ProductHeader.defaultProps = {};

const normalizeUrl = url => {
  return url
    .split(/[?#]/)[0]
    .split('/')
    .filter(str => str !== '')
    .join('/');
};

/* ======================================== END of STYLES ======================================== */

function ProductHeader({ className, hidePromo }) {
  const router = useRouter();
  const { isCountdownActive } = useCountdownTimer();
  const memberJoinDate = useSelector(getMemberJoinDate);
  const pageRenderDate = useSelector(state =>
    parseDate(state.time.pageRenderDate).getTime()
  );
  const oneHour = 60 * 60 * 1000;
  const endDate =
    (memberJoinDate ? memberJoinDate.getTime() : pageRenderDate) + oneHour;
  const assetContainerImageBanner = useAsset('products_image_banner', {
    multi: true,
  });
  const assetContainerSkinnyBanner = useAsset('products_html_skinnybanner', {
    multi: true,
  });
  const isShowingNewPricingSkinnyBanner = useShouldShowNewPricingSkinnyBanner();

  const findAsset = assets => {
    const url = normalizeUrl(router.asPath);
    if (!assets.length) {
      return false;
    }
    return assets.find(asset => {
      const {
        options: { customVars },
      } = asset;
      if (!customVars.pageUrl) {
        return false;
      }
      const assetUrl = normalizeUrl(customVars.pageUrl);
      return assetUrl === url;
    });
  };

  const renderCountdownTimer = _endDate => (
    <CountdownTimerWrapper>
      <FormattedMessage
        defaultMessage="Offer Ends In"
        id="site_product_banner.offer_ends_in"
      />{' '}
      <StyledAssetCountdown countDownEndDate={_endDate} />
    </CountdownTimerWrapper>
  );

  const renderSimpleBanner = asset => {
    const { options } = asset;
    return (
      <SimpleBanner
        background
        className={className}
        showMobile={options.customVars.showOnMobile}
        asset={asset}
      />
    );
  };

  const renderSkinnyBanner = () => {
    const { data } = assetContainerSkinnyBanner;
    const assets = data ? data.assets : [];
    const assetToShow = findAsset(assets);
    if (assetToShow) {
      const { options } = assetToShow;

      return (
        <SkinnyBanner bgColor={options.bgColor}>
          <HTMLAsset asset={assetToShow} />
        </SkinnyBanner>
      );
    }
    return null;
  };

  const renderImageBanner = () => {
    const { data } = assetContainerImageBanner;
    const assets = data ? data.assets : [];
    const assetToShow = findAsset(assets);

    if (assetToShow) {
      const { options } = assetToShow;
      // bypassing vacant asset check for post reg since post reg not directly using asset from products_image_banner
      // instead, it will look for headerContainerName and reference another asset container by that
      if (
        options.customVars.pageUrl === '/quiz-complete' &&
        options.customVars.headerContainerName
      ) {
        return (
          <>
            <QuizResultsHeaderAsset
              containerName={options.customVars.headerContainerName}
            />
            {!isCountdownActive ? null : (
              <SectionWrapper isCountdownActive={isCountdownActive}>
                <StyledCountdownTimerHeader
                  renderDefaultTimer={() => renderCountdownTimer(endDate)}
                />
              </SectionWrapper>
            )}
          </>
        );
      }
      // If asset has no content, ie if created purely for nav reasons, don't render a header
      const vacantAsset =
        !assetToShow.imageFilename && !options.bgColor && !options.htmlText;

      if (assetToShow.segmentation.id === null || vacantAsset || hidePromo) {
        return null;
      }

      if (options.customVars.isFancy) {
        return <ProductHeaderFancy className={className} asset={assetToShow} />;
      }
      return renderSimpleBanner(assetToShow);
    }
    return null;
  };

  if (isShowingNewPricingSkinnyBanner) {
    return null;
  }

  return (
    <>
      {renderImageBanner()}
      {renderSkinnyBanner()}
    </>
  );
}

ProductHeader.skinnyBannerStyles = skinnyBannerStyles;

export default ProductHeader;
